import React from "react";
import styled from "styled-components";

import GenerativeAudioThumbnail from "../media-preview/components/audio-thumbnail/GenerativeAudioThumbnail";
import { type MarkdownMedia } from "./markdownMedia";

/**
 * Markdown 本文の先頭メディア（extractLeadMedia の結果）を描くサムネイル。
 *
 * 共有ノートのカード（共有TIPS）で、個別アップロードに代わって本文の
 * 添付メディアを自動採用するために使う。種別ごとに描き分ける:
 * - 画像: そのまま cover 表示
 * - 動画: 先頭フレーム（#t=0.1）を muted の `<video>` で静止表示
 * - 音声: 生成サムネイル（GenerativeAudioThumbnail）の静止絵。
 *   ノート添付には波形 peaks が無いため動きは持たない
 *
 * サイズ・角丸は呼び出し側が className（styled 拡張）で決める。
 */

const Root = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const CoverImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const CoverVideo = styled.video`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
`;

/** 静止絵用: 再生位置は常に 0 秒（毎フレーム呼ばれるため安定参照にする）。 */
const zeroTime = () => 0;

export interface MarkdownMediaThumbnailProps {
  media: MarkdownMedia;
  className?: string;
}

const MarkdownMediaThumbnail: React.FC<MarkdownMediaThumbnailProps> = ({
  media,
  className,
}) => (
  <Root className={className}>
    {media.kind === "image" && (
      <CoverImage src={media.url} alt="" loading="lazy" />
    )}
    {media.kind === "video" && (
      <CoverVideo
        src={`${media.url}#t=0.1`}
        muted
        playsInline
        preload="metadata"
      />
    )}
    {media.kind === "audio" && (
      <GenerativeAudioThumbnail
        src={media.url}
        isPlaying={false}
        glowTrigger="none"
        getCurrentTime={zeroTime}
      />
    )}
  </Root>
);

export default MarkdownMediaThumbnail;
